import { useCallback, useEffect, useState } from 'react'
import { API_ARVORE_ENDPOINT as API_ARVORE_URL } from 'config/vars'

const getCookieValue = (name: string) => {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match ? decodeURIComponent(match[2]) : null
}

const buildHeaders = (withBody = false) => {
  const token = getCookieValue('access_token')
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  if (withBody) headers['Content-Type'] = 'application/json'
  return headers
}

type NewsLoan = {
  id: number
  articleId: number
  status: 'active' | 'returned' | 'expired'
  borrowedAt: string
  dueAt: string
}

type NewsAvailability = {
  articleId: number
  available: boolean
  userCanBorrow: boolean
  userBlockReason: string | null
  hasActiveLoan: boolean
}

export const useNewsLoan = (articleId?: number, enabled = false) => {
  const [activeLoan, setActiveLoan] = useState<NewsLoan | null>(null)
  const [availability, setAvailability] = useState<NewsAvailability | null>(
    null
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [actionLoading, setActionLoading] = useState<boolean>(false)

  const loadAvailability = useCallback(async () => {
    if (!(enabled && articleId)) return
    try {
      const response = await fetch(
        `${API_ARVORE_URL}/news-loans/articles/${articleId}/availability`,
        {
          method: 'GET',
          headers: buildHeaders(),
          credentials: 'include'
        }
      )
      if (!response.ok) return
      const body = await response.json()
      setAvailability(body?.data ?? body ?? null)
    } catch {
      setAvailability(null)
    }
  }, [articleId, enabled])

  const loadLoan = useCallback(async () => {
    if (!(enabled && articleId)) return
    setIsLoading(true)
    try {
      const response = await fetch(
        `${API_ARVORE_URL}/news-loans?status=active`,
        {
          method: 'GET',
          headers: buildHeaders(),
          credentials: 'include'
        }
      )
      if (!response.ok) return
      const body = await response.json()
      const loans: NewsLoan[] = Array.isArray(body) ? body : body?.data ?? []
      const loan = loans.find(item => item.articleId === articleId) ?? null
      setActiveLoan(loan)
    } catch {
      setActiveLoan(null)
    } finally {
      setIsLoading(false)
    }
  }, [articleId, enabled])

  useEffect(() => {
    void loadLoan()
  }, [loadLoan])

  useEffect(() => {
    void loadAvailability()
  }, [loadAvailability])

  const borrow = useCallback(async () => {
    if (!articleId) return
    setActionLoading(true)
    try {
      const response = await fetch(`${API_ARVORE_URL}/news-loans`, {
        method: 'POST',
        headers: buildHeaders(true),
        credentials: 'include',
        body: JSON.stringify({ articleId })
      })
      if (!response.ok) return
      const body = await response.json()
      setActiveLoan(body?.data ?? body ?? null)
    } finally {
      setActionLoading(false)
      await loadAvailability()
    }
  }, [articleId, loadAvailability])

  const returnLoan = useCallback(async () => {
    if (!activeLoan) return
    setActionLoading(true)
    try {
      await fetch(`${API_ARVORE_URL}/news-loans/${activeLoan.id}`, {
        method: 'DELETE',
        headers: buildHeaders(),
        credentials: 'include'
      })
      setActiveLoan(null)
      if (articleId && window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'delete_article', data: { id: articleId } })
        )
      }
    } finally {
      setActionLoading(false)
      await loadAvailability()
    }
  }, [activeLoan, articleId, loadAvailability])

  return {
    activeLoan,
    availability,
    hasActiveLoan: Boolean(activeLoan),
    isLoading,
    actionLoading,
    borrow,
    returnLoan
  }
}
